import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const apiClient = axios.create({
  baseURL: API,
  // Note: withCredentials removed as we use Bearer token auth, not cookies
  // This fixes CORS issues in Firefox/Edge with wildcard origins
  headers: {
    'Content-Type': 'application/json'
  }
});

// Add auth token to requests if available
apiClient.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle auth errors
apiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      // Don't redirect if already on auth pages
      if (!window.location.pathname.includes('/login') && 
          !window.location.pathname.includes('/register') &&
          window.location.pathname !== '/') {
        window.location.href = '/login';
      }
    }
    return Promise.reject(error);
  }
);

// Auth APIs
export const auth = {
  register: (data) => apiClient.post('/auth/register', data),
  login: (data) => apiClient.post('/auth/login', data),
  logout: () => apiClient.post('/auth/logout'),
  deleteAccount: () => apiClient.delete('/auth/account'),
  me: () => apiClient.get('/auth/me'),
  processGoogleSession: (sessionId, role) => 
    apiClient.post('/auth/session', { session_id: sessionId, role }),
  updateRole: (role) => apiClient.put('/auth/role', { role }),
  updateProfile: (data) => apiClient.put('/auth/profile', data),
  uploadPhoto: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return apiClient.post('/auth/upload-photo', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  changePassword: (currentPassword, newPassword) =>
    apiClient.put('/auth/change-password', {
      current_password: currentPassword,
      new_password: newPassword
    }),
  forgotPassword: (email) => apiClient.post('/auth/forgot-password', { email }),
  resetPassword: (token, newPassword) =>
    apiClient.post('/auth/reset-password', { token, new_password: newPassword })
};

// School APIs (Employer)
export const schools = {
  create: (data) => apiClient.post('/schools', data),
  list: () => apiClient.get('/schools'),
  get: (schoolId) => apiClient.get(`/schools/${schoolId}`),
  update: (schoolId, data) => apiClient.put(`/schools/${schoolId}`, data),
  getTOCs: (schoolId) => apiClient.get(`/schools/${schoolId}/tocs`),
  getArchivedTOCs: (schoolId) => apiClient.get(`/schools/${schoolId}/archived-tocs`),
  getPendingTOCs: (schoolId) => apiClient.get(`/schools/${schoolId}/pending-tocs`),
  approveTOC: (schoolId, tocId, action) => 
    apiClient.post(`/schools/${schoolId}/tocs/${tocId}/approve`, { action }),
  updateTOCStatus: (schoolId, tocId, status) => 
    apiClient.put(`/schools/${schoolId}/tocs/${tocId}/status`, { status }),
  updateTOCNotes: (schoolId, tocId, note) =>
    apiClient.put(`/schools/${schoolId}/tocs/${tocId}/notes`, { note }),
  updateTOCCategories: (schoolId, tocId, categories) =>
    apiClient.put(`/schools/${schoolId}/tocs/${tocId}/categories`, { categories }),
  archiveTOC: (schoolId, tocId) => apiClient.delete(`/schools/${schoolId}/tocs/${tocId}`),
  unarchiveTOC: (schoolId, tocId) => apiClient.post(`/schools/${schoolId}/tocs/${tocId}/unarchive`),
  removeTOC: (schoolId, tocId) => apiClient.delete(`/schools/${schoolId}/tocs/${tocId}/permanent`),
  getTOCUnavailability: (schoolId, tocId) => apiClient.get(`/schools/${schoolId}/tocs/${tocId}/unavailability`),
  archiveSchool: (schoolId) => apiClient.post(`/schools/${schoolId}/archive`),
  unarchiveSchool: (schoolId) => apiClient.post(`/schools/${schoolId}/unarchive`),
  getArchivedSchools: () => apiClient.get('/schools/archived')
};

// Co-Admin APIs (owner manages co-admins for a school)
export const coAdmins = {
  list: (schoolId) => apiClient.get(`/schools/${schoolId}/co-admins`),
  invite: (schoolId, email) => apiClient.post(`/schools/${schoolId}/co-admins/invite`, { email }),
  resendInvite: (schoolId, inviteId) => apiClient.post(`/schools/${schoolId}/co-admins/invites/${inviteId}/resend`),
  cancelInvite: (schoolId, inviteId) => apiClient.post(`/schools/${schoolId}/co-admins/invites/${inviteId}/cancel`),
  remove: (schoolId, coAdminId) => apiClient.delete(`/schools/${schoolId}/co-admins/${coAdminId}`),
  // Token-based invite lookup + accept
  getInvite: (token) => apiClient.get(`/co-admin-invites/${token}`),
  acceptInvite: (token) => apiClient.post(`/co-admin-invites/${token}/accept`)
};

// TOC APIs
export const toc = {
  connect: (schoolCode) => apiClient.post('/toc/connect', { school_code: schoolCode }),
  getPendingSchools: () => apiClient.get('/toc/pending-schools'),
  declineAllToday: (action) => apiClient.post('/toc/decline-all-today', { action }),
  declineDateRange: (startDate, endDate) => apiClient.post('/toc/decline-date-range', { start_date: startDate, end_date: endDate }),
  clearDeclinedDates: () => apiClient.delete('/toc/decline-dates'),
  toggleDate: (date) => apiClient.post('/toc/toggle-date', { date }),
  getAvailabilityStatus: () => apiClient.get('/toc/availability-status'),
  disconnect: (schoolId) => apiClient.delete(`/toc/disconnect/${schoolId}`)
};

// Position APIs
export const positions = {
  // Never let the post spinner hang forever: fail fast so the inline retry alert shows.
  create: (data) => apiClient.post('/positions', data, { timeout: 30000 }),
  list: (params) => apiClient.get('/positions', { params }),
  get: (positionId) => apiClient.get(`/positions/${positionId}`),
  update: (positionId, data) => apiClient.put(`/positions/${positionId}`, data),
  respond: (positionId, response) => 
    apiClient.post(`/positions/${positionId}/respond`, { response }),
  cancel: (positionId) => apiClient.delete(`/positions/${positionId}`),
  archive: (positionId) => apiClient.post(`/positions/${positionId}/archive`),
  permanentDelete: (positionId) => apiClient.delete(`/positions/${positionId}/permanent`),
  listArchived: (params) => apiClient.get('/positions', { params: { ...params, archived: true } }),
  markViewed: (positionId) => apiClient.post(`/positions/${positionId}/view`),
  getViews: (positionId) => apiClient.get(`/positions/${positionId}/views`),
  sendReminders: (positionId, force = false) => apiClient.post(`/positions/${positionId}/send-reminders?force=${force}`),
  sendAllReminders: () => apiClient.post('/positions/send-all-reminders'),
  dismiss: (positionId) => apiClient.post(`/positions/${positionId}/dismiss`),
  getAllAccepted: () => apiClient.get('/positions/all-accepted'),
  getOfferedSubs: (positionId) => apiClient.get(`/positions/${positionId}/offered-subs`)
};

// Notification APIs
export const notifications = {
  list: (unreadOnly = false) => apiClient.get('/notifications', { params: { unread_only: unreadOnly } }),
  markRead: (notificationId) => apiClient.put(`/notifications/${notificationId}/read`),
  markAllRead: () => apiClient.put('/notifications/read-all'),
  // KAN-156: account-level notification preferences (e.g. position_filled_sound)
  getPreferences: () => apiClient.get('/notifications/preferences'),
  updatePreferences: (prefs) => apiClient.put('/notifications/preferences', prefs)
};

// Analytics APIs
export const analytics = {
  getTOCAnalytics: () => apiClient.get('/analytics/toc'),
  getEmployerAnalytics: (schoolId) => apiClient.get(`/analytics/employer/${schoolId}`),
  getSubPositionHistory: (schoolId) => apiClient.get(`/analytics/employer/${schoolId}/positions`)
};

// Subscription APIs
export const subscription = {
  // includeInternal also returns non-purchasable plans (trial_premium, flagged is_internal) so
  // limit/feature lookups work for schools on them; never render those in a plan picker.
  getPlans: (schoolId, includeInternal = false) => apiClient.get('/subscription/plans', {
    params: { school_id: schoolId, ...(includeInternal ? { include_internal: true } : {}) },
  }),
  getStatus: (schoolId) => apiClient.get(`/subscription/status/${schoolId}`),
  // Account-level status (no school required) — the subscription belongs to
  // the school-admin account, so this works even with zero schools.
  getAccountStatus: () => apiClient.get('/subscription/status'),
  createCheckout: (planId, billingCycle, originUrl, returnPath) =>
    apiClient.post('/subscription/checkout', {
      plan_id: planId,
      billing_cycle: billingCycle,
      origin_url: originUrl,
      ...(returnPath ? { return_path: returnPath } : {})
    }),
  checkoutStatus: (sessionId) => apiClient.get(`/subscription/checkout/status/${sessionId}`),
  upgrade: (planId) => apiClient.post('/subscription/upgrade', { plan_id: planId }),
  downgrade: (planId) => apiClient.post('/subscription/downgrade', { plan_id: planId }),
  switchBillingCycle: (billingCycle) => apiClient.post('/subscription/billing-cycle', { billing_cycle: billingCycle }),
  cancel: () => apiClient.post('/subscription/cancel'),
  redeemTrialCoupon: (code) => apiClient.post('/subscription/trial/redeem', { code }),
  openBillingPortal: (originUrl, returnPath) =>
    apiClient.post('/subscription/billing-portal', {
      origin_url: originUrl,
      ...(returnPath ? { return_path: returnPath } : {})
    })
};

// Messaging APIs
export const messages = {
  send: (schoolId, content) => apiClient.post('/messages', { school_id: schoolId, content }),
  sendToTOC: (tocId, schoolId, content) => 
    apiClient.post(`/messages/to/${tocId}`, { school_id: schoolId, content }),
  list: (schoolId, tocId = null) => 
    apiClient.get(`/messages/${schoolId}`, { params: tocId ? { toc_id: tocId } : {} }),
  markRead: (messageId) => apiClient.put(`/messages/${messageId}/read`),
  getUnreadCount: () => apiClient.get('/messages/unread/count'),
  deleteMessage: (messageId) => apiClient.delete(`/messages/${messageId}`)
};

// Admin APIs (Subscription Manager)
export const admin = {
  getUsers: (params = {}) => apiClient.get('/admin/users', { params }),
  getSchools: (params = {}) => apiClient.get('/admin/schools', { params }),
  getPositions: (params = {}) => apiClient.get('/admin/positions', { params }),
  getAnalytics: () => apiClient.get('/admin/analytics'),
  getUserDetails: (userId) => apiClient.get(`/admin/user/${userId}`),
  getSchoolDetails: (schoolId) => apiClient.get(`/admin/school/${schoolId}`),
  // User management
  createUser: (data) => apiClient.post('/admin/users', data),
  updateUser: (userId, data) => apiClient.put(`/admin/users/${userId}`, data),
  deleteUser: (userId) => apiClient.delete(`/admin/users/${userId}`),
  resetPassword: (userId, newPassword) => apiClient.post(`/admin/users/${userId}/reset-password`, { new_password: newPassword }),
  // School management
  createSchool: (data) => apiClient.post('/admin/schools', data),
  updateSchool: (schoolId, data) => apiClient.put(`/admin/schools/${schoolId}`, data),
  deleteSchool: (schoolId) => apiClient.delete(`/admin/schools/${schoolId}`),
  exportUserData: (userId) => apiClient.get(`/admin/users/${userId}/export`),
  // Trial Premium tooling
  issueTrialCoupon: (email) => apiClient.post('/admin/trial-coupons', { email }),
  listTrialCoupons: () => apiClient.get('/admin/trial-coupons'),
  revokeTrialCoupon: (code) => apiClient.post(`/admin/trial-coupons/${encodeURIComponent(code)}/revoke`),
  listTrialAccounts: () => apiClient.get('/admin/trial-accounts'),
  getTrialFunnel: () => apiClient.get('/admin/trial-funnel'),
  endTrialNow: (employerId) => apiClient.post(`/admin/trial-accounts/${employerId}/end-trial`)
};

// Pricing lead capture (Notify me when paid plans launch)
export const pricingLeads = {
  create: (data) => apiClient.post('/pricing-leads', data),
  list: (params = {}) => apiClient.get('/pricing-leads', { params }),
  remove: (leadId) => apiClient.delete(`/pricing-leads/${leadId}`)
};

export default apiClient;

// KAN-156: real-time school-admin events. The live stream (SSE) lives at
// `${API}/events/stream`; this is the polling fallback used when it's blocked.
export const EVENTS_STREAM_URL = `${API}/events/stream`;
export const events = {
  pollPositionFilled: (since) => apiClient.get('/events/position-filled', { params: since ? { since } : {} }),
};
